import { useState } from "react";
import { toast } from "sonner";
import { Palmtree, CalendarOff, X } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

export const LEAVE_LABEL = { pending: "Leave · Pending approval", approved: "Leave · Approved", rejected: "Leave · Rejected" };

export function DayStatusCard({ ds, closed, onCleared }) {
  const clear = async () => {
    try {
      await api.delete("/day-status", { params: { date: ds.date } });
      toast.success("Status cleared");
      onCleared();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const isLeave = ds.status === "leave";
  const Icon = isLeave ? Palmtree : CalendarOff;
  return (
    <div className={`rounded-2xl border p-5 flex gap-4 items-start ${ds.leave_status === "rejected" ? "bg-red-50 border-red-200" : "bg-[#F7F3EA] border-[#E6D9B8]"}`} data-testid="day-status-card">
      <Icon className="w-7 h-7 text-[#8a6d2f] shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-display text-xl font-bold text-[#011E38]" data-testid="day-status-label">{isLeave ? LEAVE_LABEL[ds.leave_status] : "Week Off"}</div>
        <div className="text-sm text-slate-600">{fmtDate(ds.date)}{ds.reason && <> · “{ds.reason}”</>}</div>
        {ds.leave_status === "rejected" && <div className="text-sm text-red-700 mt-1">Your manager rejected this leave. Please submit your performance for this day.</div>}
        {ds.leave_status !== "rejected" && <div className="text-xs text-slate-500 mt-1">{isLeave && ds.leave_status === "pending" ? "Counts as an available day until your manager approves it." : "This day is not counted in your available sale days."}</div>}
      </div>
      {!closed && <Button variant="ghost" size="sm" onClick={clear} data-testid="day-status-clear-button"><X className="w-4 h-4 mr-1" /> Clear</Button>}
    </div>
  );
}

export function DayStatusForm({ date, status, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const leave = status === "leave";
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/day-status", { date, status, reason });
      toast.success(leave ? "Leave request sent to your manager" : "Marked as Week Off");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bs-card p-5 space-y-4" data-testid="day-status-form">
      <div className="font-display text-xl font-bold text-[#011E38]">{leave ? "Request leave" : "Mark week off"} · {fmtDate(date)}</div>
      <p className="text-sm text-slate-500">{leave ? "Your manager will approve or reject this. Approved leave is removed from your available sale days." : "This day will be removed from your available sale days and expected target."}</p>
      <Textarea placeholder={leave ? "Reason for leave (required)" : "Note (optional)"} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="day-status-reason-input" />
      <Button disabled={busy || (leave && !reason.trim())} onClick={save} className="w-full h-14 text-lg rounded-2xl bg-[#011E38] hover:bg-[#032C52]" data-testid="day-status-submit-button">
        {leave ? "Send leave request" : "Confirm week off"}
      </Button>
    </div>
  );
}
